<script lang="ts">
  // Custom collapsible timetable using BJL Collapsible.
  // Renders remaining stop list and transfer options for a vehicle.
  import { Collapsible } from '@byjackli-labs/ui/Collapsible'
  import { Colors, compareRoutes, getRouteColor, routeBadgeLabel } from '../../ui/colors'
  import { bulletFontPx, bulletText, Type } from '../../ui/theme'
  import { api, type StopTimeUpdate } from '../../services/api'
  import {
    fmtClock,
    fmtMin,
    parentStopId,
    type TimetableSource,
    type TransferContext,
  } from './timetable'
  import { useStopsStore } from '../../store/stops'
  import { storeSelect } from '../../lib/store.svelte'
  import RouteBadge from '../RouteBadge.svelte'

  const TIMETABLE_BG = Colors.ground
  const DOT_COLOR = '#ffffff'

  interface Props {
    source: TimetableSource
    /** Route badge color + label for the header strip. */
    routeId: string
    /** The stop the sheet is open on: marked "You are here", with no transfer button. */
    currentStopId?: string
    /** Callback when user chooses to transfer to a stop. */
    onTransfer: (stopId: string, stopName: string, ctx: TransferContext | null) => void
    /** Callback when trip metadata loads. */
    onMetaLoaded?: (destination: string | null, origin: string | null) => void
  }

  const { source, routeId, currentStopId, onTransfer, onMetaLoaded }: Props = $props()

  const REFRESH_MS = 30_000

  let updates = $state<StopTimeUpdate[] | null>(null)
  // Live running routes by station ID
  let liveRoutes = $state<Record<string, string[]> | null>(null)
  let loading = $state(true)
  let failed = $state(false)
  let openStopId = $state<string | null>(null)

  let previousExpanded = $state(false)

  // Stable trip keys for fetching updates.
  const kind = $derived(source.kind)
  const key = $derived(source.kind === 'bus' ? source.vehicleId : source.tripId)

  // For the transfer route badges at each station.
  const stopsData = storeSelect(useStopsStore, (s) => s.stops)

  // Maps parent station IDs to reachable transfer routes.
  type StopTransfers = {
    routes: string[]
    limited: Array<{ routes: string[]; opensHour: number; closesHour: number }>
  }
  const routesByStop = $derived.by(() => {
    const map = new Map<string, StopTransfers>()
    for (const s of stopsData.value) {
      const entry: StopTransfers = {
        routes: s.transferRoutes ?? s.routes,
        limited: s.limitedTransfers ?? [],
      }
      map.set(s.id, entry)
      for (const m of s.memberIds ?? []) map.set(m, entry)
    }
    return map
  })

  // Local wall-clock, which for a NYC transit app is the passage's own clock.
  function passageOpenAt(atSec: number, opensHour: number, closesHour: number): boolean {
    const hour = new Date(atSec * 1000).getHours()
    // A window that wraps midnight (22→2) is open on either side of it.
    if (opensHour > closesHour) return hour >= opensHour || hour < closesHour
    return hour >= opensHour && hour < closesHour
  }

  // Filter and deduplicate running station routes for transfers.
  function getStationRoutes(stopId: string, atSec: number): string[] {
    const pid = source.kind === 'subway' ? parentStopId(stopId) : stopId
    const entry = routesByStop.get(pid)
    if (!entry) return []

    // Part-time passages are judged against arrival at THAT stop, not now: a train boarded
    // at 11:40pm reaches its last stops after the corridor has shut.
    const routes = [...entry.routes]
    for (const w of entry.limited) {
      if (passageOpenAt(atSec, w.opensHour, w.closesHour)) routes.push(...w.routes)
    }

    const live = liveRoutes?.[pid]
    const running = live ? new Set(live.map(routeBadgeLabel)) : null

    const byLabel = new Map<string, string>()
    for (const r of routes) {
      const label = routeBadgeLabel(r)
      if (running && !running.has(label)) continue
      if (!byLabel.has(label) || r === label) byLabel.set(label, r)
    }
    return [...byLabel.values()].sort(compareRoutes)
  }

  // Returns transfer routes excluding current line.
  function getTransferRoutes(stopId: string, atSec: number | null): string[] {
    const base = routeBadgeLabel(routeId)
    return getStationRoutes(stopId, atSec ?? nowSec).filter((r) => routeBadgeLabel(r) !== base)
  }

  $effect(() => {
    const k = kind
    const id = key
    let live = true
    let firstLoad = true

    const fetchStops = () => {
      const req = k === 'subway'
        ? api.getTripUpdates(id)
        : k === 'ferry'
          ? api.getFerryTrip(id)
          : k === 'rail'
            ? api.getRailTrip(id)
            : k === 'njbus'
              ? api.getNjBusTrip(id)
              : api.getBusVehicleStops(id)
      return req
        .then((d) => {
          if (!live) return
          updates = d.updates
          failed = false
          // Surface destination/origin to parent on first successful load.
          if (firstLoad && onMetaLoaded && d.updates.length > 0) {
            firstLoad = false
            const dest = d.updates[d.updates.length - 1].stopName ?? null
            const currentNorm = currentStopId
              ? (k === 'subway' ? parentStopId(currentStopId) : currentStopId)
              : null
            let orig: string | null = null
            if (currentNorm) {
              const here = d.updates.find((u) =>
                (k === 'subway' ? parentStopId(u.stopId) : u.stopId) === currentNorm,
              )
              if (here) orig = here.stopName ?? null
            }
            if (!orig) orig = d.updates[0].stopName ?? null
            onMetaLoaded(dest, orig)
          }
        })
        .catch(() => {
          if (!live) return
          updates = []
          failed = true
        })
        .finally(() => {
          if (live) loading = false
        })
    }

    // Refreshed alongside stop updates.
    const fetchLive = () => {
      if (k !== 'subway') return
      api
        .getLiveStationRoutes()
        .then((d) => {
          if (!live) return
          if (Object.keys(d.stops).length > 0) liveRoutes = d.stops
        })
        .catch(() => {})
    }

    loading = true
    openStopId = null
    previousExpanded = false
    fetchStops()
    fetchLive()
    const timer = setInterval(() => { fetchStops(); fetchLive() }, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  const color = $derived(getRouteColor(routeId))
  // Normalize stop ID
  const norm = (id: string) => (source.kind === 'subway' ? parentStopId(id) : id)
  const current = $derived(currentStopId ? norm(currentStopId) : null)

  // Live clock tick
  let nowSec = $state(Math.floor(Date.now() / 1000))

  $effect(() => {
    const interval = setInterval(() => {
      nowSec = Math.floor(Date.now() / 1000)
    }, 1000)
    return () => clearInterval(interval)
  })

  interface TrainPosition {
    kind: 'at_stop' | 'in_between'
    index: number
  }

  // Either at a specific stop, or en route between two.
  const trainPosition = $derived.by<TrainPosition>(() => {
    if (!updates || updates.length === 0) {
      return { kind: 'at_stop', index: 0 }
    }
    const now = nowSec

    for (let i = 0; i < updates.length; i++) {
      const u = updates[i]
      const arr = u.arrivalTime ?? u.departureTime
      const dep = u.departureTime ?? u.arrivalTime

      if (arr == null && dep == null) continue

      if (arr != null && now < arr) {
        if (i === 0) {
          return { kind: 'at_stop', index: 0 }
        }
        let prevDep: number | null = null
        for (let j = i - 1; j >= 0; j--) {
          const d = updates[j].departureTime ?? updates[j].arrivalTime
          if (d != null) {
            prevDep = d
            break
          }
        }
        if (prevDep != null && now >= prevDep) {
          return { kind: 'in_between', index: i - 1 }
        } else {
          return { kind: 'at_stop', index: i - 1 }
        }
      }

      if (dep != null && now <= dep) {
        return { kind: 'at_stop', index: i }
      }
    }

    return { kind: 'at_stop', index: updates.length - 1 }
  })

  const firstRemainingIndex = $derived(
    trainPosition.kind === 'at_stop' ? trainPosition.index : trainPosition.index + 1,
  )
  const hasPrevious = $derived(firstRemainingIndex > 0)
</script>

{#if loading && !updates}
  <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">Loading timetable…</div>
{:else if !updates || updates.length === 0}
  <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
    {failed
      ? 'Could not load this vehicle\u2019s timetable.'
      : 'No stops reported for this vehicle.'}
  </div>
{:else}
  <!-- Previous stops toggle button -->
  {#if hasPrevious}
    <button
      onclick={() => { previousExpanded = !previousExpanded }}
      style="
        display: flex; align-items: center; justify-content: space-between;
        width: 100%; background: none; border: none; cursor: pointer;
        color: {Colors.textMuted}; {Type.eyebrow};
        padding: 4px 0 8px; margin-bottom: 6px; text-align: left;
      "
      aria-expanded={previousExpanded}
    >
      <span>Previous stops ({firstRemainingIndex})</span>
      <span style="font-size: 11px; font-weight: 700; color: {Colors.textSecondary}; display: flex; align-items: center; gap: 4px;">
        {previousExpanded ? 'Hide' : 'Show'}
        <span style="font-size: 12px;">{previousExpanded ? '▴' : '▾'}</span>
      </span>
    </button>
  {/if}

  <!-- Route line spine -->
  <div style="display: flex; flex-direction: column; position: relative;">
    <div
      aria-hidden="true"
      style="
        position: absolute; top: 0; bottom: 0; left: 18px;
        width: 4px; background-color: {color};
      "
    ></div>

    {#each updates as u, i (`${u.stopId}:${i}`)}
      {@const isPast = trainPosition.kind === 'at_stop' ? i < trainPosition.index : i <= trainPosition.index}
      {@const isHere = trainPosition.kind === 'at_stop' && i === trainPosition.index}
      {@const isUserStation = current !== null && norm(u.stopId) === current}
      {@const isOpen = openStopId === u.stopId}
      {@const time = u.arrivalTime ?? u.departureTime}
      {@const name = u.stopName ?? u.stopId}
      {@const delaySec = u.arrivalDelay ?? u.departureDelay ?? null}
      {@const lateMin = delaySec != null ? Math.round(delaySec / 60) : 0}
      {@const transferRoutes = getTransferRoutes(u.stopId, time)}
      {@const isBetweenTrain = trainPosition.kind === 'in_between' && trainPosition.index === i}

      {#if !isPast || previousExpanded}
        {#if hasPrevious && trainPosition.kind === 'at_stop' && i === firstRemainingIndex && previousExpanded}
          <div
            style="
              margin-top: 8px; margin-bottom: 6px;
              border-top: 1px solid rgba(255,255,255,0.2);
              position: relative; z-index: 1;
            "
          ></div>
        {/if}

      <Collapsible
        variant="ghost"
        size="sm"
        open={isOpen}
        disabled={isHere || isPast}
        onToggle={(o) => { openStopId = o ? u.stopId : null }}
        ariaLabel={isHere ? `${name}, you are here` : isPast ? `${name}, stop passed` : `Transfer options at ${name}`}
      >
        {#snippet trigger({ open })}
          <div
            style="
              display: flex; align-items: center; width: 100%; text-align: left;
              padding: 0 8px; border-radius: {open ? '8px 8px 0 0' : '8px'};
              background-color: {open ? 'rgba(255, 255, 255, 0.08)' : 'transparent'};
              transition: background-color 0.15s ease, border-radius 0.15s ease;
            "
          >
            <!-- Stop dot -->
            <div
              style="
                position: relative; z-index: 1; width: 24px; flex-shrink: 0;
                display: flex; align-items: center; justify-content: center;
              "
            >
              {#if isHere}
                <div style="width: 16px; height: 16px; border-radius: 8px; background-color: {DOT_COLOR};"></div>
              {:else if isPast}
                <div
                  style="
                    width: 10px; height: 10px; border-radius: 5px; box-sizing: border-box;
                    background-color: {open ? color : 'rgba(255, 255, 255, 0.2)'};
                    border: 2px solid {open ? DOT_COLOR : 'rgba(255, 255, 255, 0.4)'};
                    transition: background-color 0.15s ease;
                  "
                ></div>
              {:else}
                <div
                  style="
                    width: 12px; height: 12px; border-radius: 6px; box-sizing: content-box;
                    background-color: {open ? color : TIMETABLE_BG};
                    border: 3px solid {DOT_COLOR};
                    transition: background-color 0.15s ease;
                  "
                ></div>
              {/if}
            </div>

            <!-- Stop name + badges -->
            <div style="flex: 1; min-width: 0; padding: 11px 0 11px 12px;">
              <div
                style="
                  color: {isPast ? Colors.textMuted : Colors.text};
                  {isHere || isUserStation ? Type.title : Type.bodyLg};
                  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                "
              >{name}</div>
              {#if isHere}
                <div style="color: {Colors.textSecondary}; {Type.caption}; margin-top: 2px;">
                  {isUserStation ? 'You are here' : 'Train here'}
                </div>
              {:else if isUserStation}
                <div style="color: {Colors.primary}; {Type.caption}; font-weight: 700; margin-top: 2px;">
                  Your station
                </div>
              {/if}
              <!-- Transfer route badges -->
              {#if transferRoutes.length > 0}
                <div style="display: flex; gap: 4px; margin-top: 5px; flex-wrap: wrap;">
                  {#each transferRoutes as tr (tr)}
                    <RouteBadge routeId={tr} size={20} style="opacity: {isPast ? 0.6 : 1};" />
                  {/each}
                </div>
              {/if}
            </div>

            <!-- Arrival time -->
            <div style="text-align: right; flex-shrink: 0; padding: 11px 0; min-width: 60px;">
              <div style="color: {isPast ? Colors.textMuted : Colors.text}; {Type.bodyStrong};">
                {fmtClock(time)}
              </div>
              <div
                style="color: {isPast ? Colors.textMuted : lateMin >= 2 ? Colors.danger : Colors.textMuted}; {Type.caption}; margin-top: 2px;"
              >
                {isPast ? 'Departed' : lateMin >= 2 ? `${lateMin} min late` : fmtMin(time)}
              </div>
            </div>
          </div>
        {/snippet}

        {#snippet children({ open })}
          {#if open && !isHere && !isPast}
            <!-- Transfer action button -->
            <div
              style="
                padding: 2px 8px 10px 28px;
                background-color: rgba(255, 255, 255, 0.08);
                border-radius: 0 0 8px 8px;
              "
            >
              <button
                onclick={() =>
                  onTransfer(
                    u.stopId,
                    name,
                    time != null ? { arriveAt: time, routeId, stopName: name } : null,
                  )}
                style="
                  display: flex; align-items: center; gap: 10px; width: 100%;
                  background: {Colors.text}; color: #000;
                  border: none; padding: 10px 14px; cursor: pointer; text-align: left;
                "
              >
                <span style="{Type.caption}; letter-spacing: 0.08em; white-space: nowrap;">TRANSFER HERE</span>
                <span style="margin-left: auto; font-size: 14px; font-weight: 700; flex-shrink: 0;">→</span>
              </button>
            </div>
          {/if}
        {/snippet}
      </Collapsible>
      {/if}

      {#if isBetweenTrain}
        {#if hasPrevious && previousExpanded}
          <div
            style="
              margin-top: 8px; margin-bottom: 6px;
              border-top: 1px solid rgba(255,255,255,0.2);
              position: relative; z-index: 1;
            "
          ></div>
        {/if}
        {@const nextStop = updates[i + 1]}
        {@const nextName = nextStop ? (nextStop.stopName ?? nextStop.stopId) : ''}
        <div
          style="
            display: flex; align-items: center; width: 100%;
            padding: 8px 8px; position: relative; z-index: 1;
          "
        >
          <!-- Stop dot sitting on the spine in between the two stops -->
          <div
            style="
              position: relative; z-index: 1; width: 24px; flex-shrink: 0;
              display: flex; align-items: center; justify-content: center;
            "
          >
            <div
              style="
                width: 16px; height: 16px; border-radius: 8px;
                background-color: {DOT_COLOR};
                box-shadow: 0 0 6px rgba(255, 255, 255, 0.6);
              "
            ></div>
          </div>
          <!-- En-route label -->
          <div style="flex: 1; min-width: 0; padding-left: 12px;">
            <span
              style="
                color: {Colors.textSecondary}; {Type.caption};
                font-weight: 600;
              "
            >
              En route{nextName ? ` to ${nextName}` : ''}
            </span>
          </div>
        </div>
      {/if}
    {/each}
  </div>
{/if}
